import { Component, computed, input, output, signal } from '@angular/core';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { FILE_TYPE } from 'src/app/@core/models/enums';
import { IAttachment } from 'src/app/@core/models/model';
import { DOCUMENT_MODEL } from 'src/app/contract-management/models/contract-management.model';
import { ChatAttachment } from '../../../models/types';
import { GeneralAgentService } from '../../../services/general-agent.service';
import { AttachmentGroupComponent } from '../../../../../@shared/components/attachment-group/attachment-group.component';
import { resolveKnowledgeCategory } from '../../../../../@shared/components/attachment-group/attachment-group-filter-types';

@Component({
    selector: 'app-thread-files',
    standalone: true,
    imports: [ButtonComponent, AttachmentGroupComponent],
    templateUrl: './thread-files.component.html',
    styleUrl: './thread-files.component.scss'
})
export class ThreadFilesComponent {

    threadId = input<string>('');
    fileOpened = output<any>();

    showThreadFiles = signal<boolean>(false);

    files = signal<(IAttachment & { docModel: DOCUMENT_MODEL, refDocModel: DOCUMENT_MODEL, refFile: string })[]>([]);
    loadingFiles = signal<boolean>(false);

    constructor(private generalAgentService: GeneralAgentService) { }

    ngOnInit(): void {
    }

    toggleThreadFiles() {
        this.showThreadFiles.set(!this.showThreadFiles());
        if (this.showThreadFiles() && this.threadId()) {
            this.loadFilesForThread(this.threadId());
        }
    }

    openFile(file: IAttachment) {
        this.showThreadFiles.set(false);
        this.fileOpened.emit(file);
    }

    private loadFilesForThread(id: string) {
        this.loadingFiles.set(true);
        this.generalAgentService.getThreadFiles(id).then(res => {
            if (res.success) {
                this.files.set(res.data);
            }
            this.loadingFiles.set(false);
        });
    }

    chatAttachments = computed<ChatAttachment[]>(() => {
        return this.files().map(file => {
            const docModel = file.refDocModel ?? file.docModel;
            return {
                _id: file._id,
                docModel,
                name: file.filename,
                isGenerated: (file as any)?.isGenerated,
                type: this.mapStringToFileType(file.type),
                attachmentData: file,
                group: (file as any).group,
                category: docModel === DOCUMENT_MODEL.REPOSITORY
                    ? resolveKnowledgeCategory((file as any).knowledgeScope, (file as any).metadata)
                    : undefined
            };
        });
    });

    private mapStringToFileType(type: string): FILE_TYPE {
        const normalizedType = type.toLowerCase();

        // Strict MIME type validation
        switch (normalizedType) {
            case 'application/pdf':
                return FILE_TYPE.PDF;
            case 'application/msword':
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                return FILE_TYPE.WORD;
            case 'application/vnd.ms-excel':
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                return FILE_TYPE.EXCEL;
            case 'application/vnd.ms-powerpoint':
            case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
                return FILE_TYPE.POWERPOINT;
            case 'text/html':
                return FILE_TYPE.HTML;
            case 'text/plain':
                return FILE_TYPE.TEXT;
            case 'text/csv':
            case 'application/csv':
                return FILE_TYPE.CSV;
            case 'text/markdown':
                return FILE_TYPE.MARKDOWN;
            case 'text/x-python':
            case 'application/x-python-code':
                return FILE_TYPE.PYTHON;
            case 'application/json':
                return FILE_TYPE.JSON;
            case 'image/jpeg':
                return FILE_TYPE.JPEG;
            case 'image/png':
                return FILE_TYPE.PNG;
            case 'image/jpg':
                return FILE_TYPE.JPG;
        }

        // Strict file extension validation
        const extension = normalizedType.split('.').pop() || normalizedType;
        switch (extension) {
            case 'pdf':
                return FILE_TYPE.PDF;
            case 'doc':
            case 'docx':
                return FILE_TYPE.WORD;
            case 'xls':
            case 'xlsx':
                return FILE_TYPE.EXCEL;
            case 'ppt':
            case 'pptx':
                return FILE_TYPE.POWERPOINT;
            case 'html':
            case 'htm':
                return FILE_TYPE.HTML;
            case 'txt':
                return FILE_TYPE.TEXT;
            case 'csv':
                return FILE_TYPE.CSV;
            case 'md':
                return FILE_TYPE.MARKDOWN;
            case 'py':
                return FILE_TYPE.PYTHON;
            case 'json':
                return FILE_TYPE.JSON;
            default:
                return FILE_TYPE.PDF; // Default fallback
        }
    }

    onOpenAttachment(attachment: (IAttachment)) {
        this.openFile(attachment);
    }
}
